import { computed, reactive, ref } from 'vue'
import { defineStore } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'
import type { PageDefinition } from '@/types/page'
import { pageByPath } from '@/data/registry'

const TABS_KEY = 'kanghu.admin.tabs.v1'
const PAGE_STATE_KEY = 'kanghu.admin.page-state.v1'
const COLUMN_KEY = 'kanghu.admin.columns.v1'

export interface WorkspaceTab {
  key: string
  path: string
  fullPath: string
  title: string
  group: string
  pinned?: boolean
}

export interface PageUiState {
  keyword: string
  status: string
  filters: Record<string, string | string[] | number | boolean | undefined>
  dateStart: string
  dateEnd: string
  page: number
  pageSize: number
  sortField: string
  sortOrder: 'asc' | 'desc'
  scrollY: number
  tableScrollTop: number
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.sessionStorage.getItem(key)
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Session storage is a convenience for UI continuity; data remains in memory.
  }
}

export function emptyPageState(pageSize = 20): PageUiState {
  return {
    keyword: '',
    status: '',
    filters: {},
    dateStart: '',
    dateEnd: '',
    page: 1,
    pageSize,
    sortField: '',
    sortOrder: 'asc',
    scrollY: 0,
    tableScrollTop: 0,
  }
}

export const useWorkspaceStore = defineStore('workspace', () => {
  const dashboard: WorkspaceTab = {
    key: '/dashboard',
    path: '/dashboard',
    fullPath: '/dashboard',
    title: '工作台',
    group: '仪表盘',
    pinned: true,
  }
  const savedTabs = readJson<WorkspaceTab[]>(TABS_KEY, [])
  const tabs = ref<WorkspaceTab[]>(savedTabs.length ? ensureDashboard(savedTabs) : [dashboard])
  const activeKey = ref('/dashboard')
  const closed = ref<WorkspaceTab[]>([])
  const pageStates = reactive<Record<string, PageUiState>>(readJson(PAGE_STATE_KEY, {}))
  const columns = reactive<Record<string, string[]>>(readJson(COLUMN_KEY, {}))

  function ensureDashboard(list: WorkspaceTab[]): WorkspaceTab[] {
    if (list.some(tab => tab.key === dashboard.key)) return list
    return [dashboard, ...list]
  }

  function persistTabs(): void {
    writeJson(TABS_KEY, tabs.value)
  }

  function persistPageStates(): void {
    writeJson(PAGE_STATE_KEY, pageStates)
  }

  function persistColumns(): void {
    writeJson(COLUMN_KEY, columns)
  }

  function visit(route: RouteLocationNormalized): void {
    if (route.path === '/login') return
    const page = pageByPath(route.path)
    const title = String(page?.title ?? route.meta.title ?? '页面')
    const group = String(page?.group ?? '基础页面')
    const existing = tabs.value.find(tab => tab.key === route.path)
    if (existing) {
      existing.fullPath = route.fullPath
      existing.title = title
      existing.group = group
    } else {
      tabs.value.push({ key: route.path, path: route.path, fullPath: route.fullPath, title, group, pinned: false })
    }
    activeKey.value = route.path
    persistTabs()
  }

  function activate(path: string): void {
    activeKey.value = path
  }

  function close(path: string): string {
    const index = tabs.value.findIndex(tab => tab.key === path)
    if (index < 0) return activeKey.value
    const tab = tabs.value[index]!
    if (tab.pinned) return activeKey.value
    tabs.value.splice(index, 1)
    closed.value.unshift(tab)
    if (closed.value.length > 8) closed.value.length = 8
    if (activeKey.value === path) {
      const next = tabs.value[Math.max(0, index - 1)] ?? tabs.value[0] ?? dashboard
      activeKey.value = next.key
      persistTabs()
      return next.fullPath
    }
    persistTabs()
    return activeKey.value
  }

  function closeOthers(path: string): string {
    tabs.value = tabs.value.filter(tab => tab.pinned || tab.key === path)
    activeKey.value = path
    persistTabs()
    return path
  }

  function closeAll(): string {
    tabs.value = tabs.value.filter(tab => tab.pinned)
    if (!tabs.value.length) tabs.value = [dashboard]
    activeKey.value = tabs.value[0]!.key
    persistTabs()
    return tabs.value[0]!.fullPath
  }

  function reopen(): WorkspaceTab | undefined {
    const tab = closed.value.shift()
    if (!tab) return undefined
    if (!tabs.value.some(item => item.key === tab.key)) tabs.value.push(tab)
    activeKey.value = tab.key
    persistTabs()
    return tab
  }

  function reorder(from: number, to: number): void {
    if (from === to || from < 0 || to < 0 || from >= tabs.value.length || to >= tabs.value.length) return
    const [tab] = tabs.value.splice(from, 1)
    if (tab) tabs.value.splice(to, 0, tab)
    persistTabs()
  }

  function resetTabs(): void {
    tabs.value = [dashboard]
    activeKey.value = dashboard.key
    closed.value = []
    persistTabs()
  }

  function hasState(pageId: string): boolean {
    return Boolean(pageStates[pageId])
  }

  function stateFor(pageId: string, pageSize = 20): PageUiState {
    return pageStates[pageId] ? { ...pageStates[pageId]!, filters: { ...pageStates[pageId]!.filters } } : emptyPageState(pageSize)
  }

  function saveState(pageId: string, patch: Partial<PageUiState>): void {
    const current = pageStates[pageId] ?? emptyPageState()
    pageStates[pageId] = { ...current, ...patch, filters: { ...current.filters, ...(patch.filters ?? {}) } }
    persistPageStates()
  }

  function clearState(pageId: string): void {
    delete pageStates[pageId]
    persistPageStates()
  }

  function clearAllStored(): void {
    for (const key of Object.keys(pageStates)) delete pageStates[key]
    for (const key of Object.keys(columns)) delete columns[key]
    resetTabs()
    persistPageStates()
    persistColumns()
  }

  function visibleColumns(page: PageDefinition): string[] {
    return columns[page.id]?.length ? [...columns[page.id]!] : page.columns.map(column => column.key)
  }

  function saveColumns(pageId: string, keys: string[]): void {
    columns[pageId] = keys
    persistColumns()
  }

  const activeTab = computed(() => tabs.value.find(tab => tab.key === activeKey.value) ?? tabs.value[0] ?? dashboard)

  return {
    tabs,
    activeKey,
    closed,
    columns,
    activeTab,
    visit,
    activate,
    close,
    closeOthers,
    closeAll,
    reopen,
    reorder,
    resetTabs,
    hasState,
    stateFor,
    saveState,
    clearState,
    clearAllStored,
    visibleColumns,
    saveColumns,
  }
})
